import { useToast } from "@/components/ui/use-toast";
import {
  fetchConnectLibraryPresignedUrl,
  useCommitConnectLibraryAnswer,
  useDeleteConnectLibraryAnswer,
  type ConnectLibraryAnswer,
} from "@/lib/api/connect-library";
import { useAudioPlayback } from "@/lib/hooks/use-audio-playback";
import { useAudioRecorder } from "@/lib/hooks/use-audio-recorder";
import { useAudioTranscoder } from "@/lib/hooks/use-audio-transcoder";
import {
  useAudioBlob,
  useLiveAudioWaveform,
  useStaticAudioWaveform,
} from "@/lib/hooks/use-audio-waveform";
import { usePostHogEvent } from "@/lib/posthog";
import type { ConnectLevel } from "@somethings-ui/core/connect-questions";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { RecorderUiState } from "./row-audio-recorder";

const MAX_DURATION_MS = 60_000;

type UploadStage = "idle" | "transcoding" | "uploading" | "saving";

interface UseConnectLibraryRowOptions {
  mentorshipId: string;
  level: ConnectLevel;
  questionId: string;
  libraryAnswer: ConnectLibraryAnswer | null;
  micDeviceId: string | null;
  onRecordingStateChange: (isRecording: boolean) => void;
  onLocalBlobChange: (hasBlob: boolean) => void;
}

export interface ConnectLibraryRow {
  uiState: RecorderUiState;
  isDeletePending: boolean;
  isDeleteMutationPending: boolean;
  start: () => void;
  stop: () => void;
  play: () => void;
  pause: () => void;
  reRecord: () => void;
  requestDelete: () => void;
  confirmDelete: () => void;
  cancelDelete: () => void;
  retryPermission: () => void;
}

export function useConnectLibraryRow({
  mentorshipId,
  level,
  questionId,
  libraryAnswer,
  micDeviceId,
  onRecordingStateChange,
  onLocalBlobChange,
}: UseConnectLibraryRowOptions): ConnectLibraryRow {
  const { toast } = useToast();
  const recorder = useAudioRecorder({
    maxDurationMs: MAX_DURATION_MS,
    deviceId: micDeviceId,
  });
  const transcoder = useAudioTranscoder();
  const commitMutation = useCommitConnectLibraryAnswer();
  const deleteMutation = useDeleteConnectLibraryAnswer();
  const { dispatchEvent } = usePostHogEvent("activity.mentor");

  const [uploadStage, setUploadStage] = useState<UploadStage>("idle");
  const [localCompletedBlob, setLocalCompletedBlob] = useState<Blob | null>(
    null,
  );
  const [pendingDelete, setPendingDelete] = useState(false);

  const liveBars = useLiveAudioWaveform(recorder.stream);

  const libraryUrlForBlob =
    !localCompletedBlob && !recorder.result
      ? (libraryAnswer?.audioUrl ?? null)
      : null;
  const libraryBlob = useAudioBlob(libraryUrlForBlob);

  const playbackBlob = useMemo(() => {
    if (recorder.state === "stopped" && recorder.result) {
      return recorder.result.blob;
    }
    if (localCompletedBlob) return localCompletedBlob;
    return libraryBlob;
  }, [recorder.state, recorder.result, localCompletedBlob, libraryBlob]);

  const staticBars = useStaticAudioWaveform(playbackBlob);

  useEffect(() => {
    onRecordingStateChange(recorder.state === "recording");
  }, [recorder.state, onRecordingStateChange]);

  useEffect(() => {
    onLocalBlobChange(localCompletedBlob !== null);
  }, [localCompletedBlob, onLocalBlobChange]);

  const playbackUrl = useMemo(() => {
    if (localCompletedBlob) return URL.createObjectURL(localCompletedBlob);
    return libraryAnswer?.audioUrl ?? null;
  }, [localCompletedBlob, libraryAnswer?.audioUrl]);

  useEffect(() => {
    if (!playbackUrl?.startsWith("blob:")) return;
    return () => URL.revokeObjectURL(playbackUrl);
  }, [playbackUrl]);

  const {
    isPlaying,
    progressMs,
    play,
    pause,
  } = useAudioPlayback(playbackUrl);

  const runCommit = useCallback(
    async (sourceBlob: Blob, durationMs: number) => {
      try {
        setUploadStage("transcoding");
        const m4a = await transcoder.transcodeToM4a(sourceBlob);

        setUploadStage("uploading");
        const presigned = await fetchConnectLibraryPresignedUrl(questionId);
        const putResponse = await fetch(presigned.presigned_url, {
          method: "PUT",
          headers: { "Content-Type": presigned.content_type },
          body: m4a,
        });
        if (!putResponse.ok) {
          throw new Error(`S3 upload failed (${putResponse.status})`);
        }

        setUploadStage("saving");
        await commitMutation.mutateAsync({
          questionId,
          audioS3Key: presigned.audio_s3_key,
          durationMs,
        });

        setLocalCompletedBlob(m4a);
        dispatchEvent("mentor_chat_connect_record_end", {
          mentorshipId,
          questionIndex: level - 1,
          questionId,
          level,
          durationSeconds: Math.round(durationMs / 1000),
        });
      } catch (err) {
        console.error("[connect-row-recorder] commit pipeline failed", err);
        const message =
          err instanceof Error && err.message
            ? err.message
            : "Something went wrong. Try again.";
        toast({
          title: "Couldn't save your recording",
          description: message,
          variant: "destructive",
        });
        recorder.reset();
        setLocalCompletedBlob(null);
      } finally {
        setUploadStage("idle");
      }
    },
    [
      questionId,
      transcoder,
      commitMutation,
      recorder,
      toast,
      dispatchEvent,
      mentorshipId,
      level,
    ],
  );

  useEffect(() => {
    if (
      recorder.state === "stopped" &&
      recorder.result &&
      uploadStage === "idle"
    ) {
      const { blob, durationMs } = recorder.result;
      void runCommit(blob, durationMs);
    }
    // Excluding runCommit prevents re-fire if its identity changes mid-upload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder.state, recorder.result]);

  const start = useCallback(() => {
    const isRerecord = !!libraryAnswer || localCompletedBlob !== null;
    dispatchEvent("mentor_chat_connect_record_start", {
      mentorshipId,
      questionIndex: level - 1,
      questionId,
      level,
      isRerecord,
    });
    void recorder.start();
  }, [
    recorder,
    dispatchEvent,
    mentorshipId,
    level,
    questionId,
    libraryAnswer,
    localCompletedBlob,
  ]);

  const stop = useCallback(() => {
    recorder.stop();
  }, [recorder]);

  const reRecord = useCallback(() => {
    dispatchEvent("mentor_chat_connect_record_rerecord", {
      mentorshipId,
      questionIndex: level - 1,
      questionId,
    });
    setLocalCompletedBlob(null);
    recorder.reset();
    dispatchEvent("mentor_chat_connect_record_start", {
      mentorshipId,
      questionIndex: level - 1,
      questionId,
      level,
      isRerecord: true,
    });
    void recorder.start();
  }, [recorder, dispatchEvent, mentorshipId, level, questionId]);

  const requestDelete = useCallback(() => {
    if (libraryAnswer) {
      setPendingDelete(true);
      return;
    }
    setLocalCompletedBlob(null);
    recorder.reset();
  }, [libraryAnswer, recorder]);

  const confirmDelete = useCallback(() => {
    if (!libraryAnswer) return;
    setPendingDelete(false);
    deleteMutation.mutate(
      { libraryAnswerId: libraryAnswer.libraryAnswerId },
      {
        onSuccess: () => {
          dispatchEvent("mentor_chat_connect_record_delete", {
            mentorshipId,
            questionIndex: level - 1,
            questionId,
          });
          setLocalCompletedBlob(null);
          recorder.reset();
        },
      },
    );
  }, [
    libraryAnswer,
    deleteMutation,
    recorder,
    dispatchEvent,
    mentorshipId,
    level,
    questionId,
  ]);

  const cancelDelete = useCallback(() => setPendingDelete(false), []);

  const uiState = useMemo<RecorderUiState>(
    () =>
      mapToRecorderUiState({
        recorderError: recorder.error,
        recorderState: recorder.state,
        recorderDurationMs: recorder.durationMs,
        recorderResultDurationMs: recorder.result?.durationMs ?? null,
        liveBars,
        uploadStage,
        hasLocalBlob: localCompletedBlob !== null,
        libraryAnswer,
        staticBars,
        isPlaying,
        progressMs,
      }),
    [
      recorder.error,
      recorder.state,
      recorder.durationMs,
      recorder.result,
      liveBars,
      uploadStage,
      localCompletedBlob,
      libraryAnswer,
      staticBars,
      isPlaying,
      progressMs,
    ],
  );

  return {
    uiState,
    isDeletePending: pendingDelete,
    isDeleteMutationPending: deleteMutation.isPending,
    start,
    stop,
    play,
    pause,
    reRecord,
    requestDelete,
    confirmDelete,
    cancelDelete,
    retryPermission: start,
  };
}

interface MapToUiStateInput {
  recorderError: ReturnType<typeof useAudioRecorder>["error"];
  recorderState: ReturnType<typeof useAudioRecorder>["state"];
  recorderDurationMs: number;
  recorderResultDurationMs: number | null;
  liveBars: number[];
  uploadStage: UploadStage;
  hasLocalBlob: boolean;
  libraryAnswer: ConnectLibraryAnswer | null;
  staticBars: number[] | null;
  isPlaying: boolean;
  progressMs: number;
}

const mapToRecorderUiState = ({
  recorderError,
  recorderState,
  recorderDurationMs,
  recorderResultDurationMs,
  liveBars,
  uploadStage,
  hasLocalBlob,
  libraryAnswer,
  staticBars,
  isPlaying,
  progressMs,
}: MapToUiStateInput): RecorderUiState => {
  if (recorderError?.kind === "permission_denied") {
    return { kind: "permission_denied" };
  }
  if (recorderError?.kind === "unsupported") {
    return { kind: "unsupported" };
  }
  if (recorderState === "starting") {
    return { kind: "starting" };
  }
  if (recorderState === "recording") {
    return {
      kind: "recording",
      durationMs: recorderDurationMs,
      bars: liveBars,
    };
  }
  if (uploadStage !== "idle") {
    return {
      kind: "uploading",
      durationMs: recorderDurationMs,
      stage: uploadStage,
    };
  }
  if (hasLocalBlob || libraryAnswer) {
    return {
      kind: "completed",
      durationMs:
        recorderResultDurationMs ?? libraryAnswer?.durationMs ?? 0,
      bars: staticBars,
      isPlaying,
      progressMs,
    };
  }
  return { kind: "default" };
};
